import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FlaskConical, Layers, ExternalLink, Eye, EyeOff, Lock, Unlock, X, Link2 } from "lucide-react";

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* storage blocked (e.g. iframe/private mode) */ } };

const rise = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } };
const Sec = ({ children, className = "s-sec" }) => (
  <motion.div className={className} variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}>{children}</motion.div>
);

const tools = [
  {
    key: "policy",
    Icon: Layers,
    eyebrow: "Interactive tool · Created by Paulyn Duman",
    title: "The Policy Function",
    desc: "An interactive decision tool for policy design that starts with function, not form: 34 mechanism cards, decision drills, a five-level impact ladder, and a full facilitator kit — grounded in implementation research, for anyone shaping reform that has to change what institutions actually do, not just launch another instrument.",
    url: "https://policy-cards-studio.emergent.host/?utm_source=paulynduman.com",
  },
  {
    key: "learning",
    Icon: FlaskConical,
    eyebrow: "Interactive tool · Created by Paulyn Duman",
    title: "The Learning Function",
    desc: "An interactive evidence tool for designing learning that actually works: 28 mechanism cards, decision drills, an impact ladder, and a full facilitator kit — built on the training-transfer research, for anyone designing capacity development that has to change what people do on the job.",
    url: "https://science-mastery-lab-2.emergent.host/?utm_source=paulynduman.com",
  },
];

const ToolGate = ({ tool, onClose, onUnlocked }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tool-access`, { email, tool: tool.title });
      safeSet(`tool_email_${tool.key}`, email);
      onUnlocked();
    } catch (err) {
      setErrMsg(err.response?.status === 422
        ? "This email domain doesn't appear to receive mail. Please check for typos."
        : "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <motion.div className="pj-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="tool-gate-backdrop">
      <motion.div className="asda-gate" initial={{ scale: 0.92, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()} data-testid="tool-gate-modal">
        <button className="pj-modal-close asda-gate-close" onClick={onClose} data-testid="tool-gate-close"><X className="icon" /></button>
        <div className="asda-gate-icon"><Lock className="icon" /></div>
        <div className="asda-gate-title">Access {tool.title}</div>
        <p className="asda-gate-text">Enter your email address to open this interactive tool. You'll only need to do this once for this tool.</p>
        <p className="asda-gate-video">Requested: <strong>{tool.title}</strong></p>
        <form onSubmit={submit}>
          <input type="email" required placeholder="Your email address" value={email} data-testid="tool-email-input"
            onChange={(e) => setEmail(e.target.value)} />
          <button type="submit" className="cf-submit" disabled={status === "sending"} data-testid="tool-unlock-btn">
            <Unlock className="icon" /> {status === "sending" ? "Unlocking..." : "Unlock tool"}
          </button>
        </form>
        {status === "error" && <p className="cf-status err" data-testid="tool-gate-error">{errMsg}</p>}
      </motion.div>
    </motion.div>
  );
};

const timeline = [
  {
    year: "2014–2016 · Origin",
    title: "Post-2015 Learning Hub, SD Talks, SDG Primer — the infrastructure and convening moment",
    body: "Before there was a platform, there was a hub. The Post-2015 Learning Hub was built at the juncture between the Millennium Development Goals and the SDGs, on the conviction that the UN system needed shared learning infrastructure rather than siloed training programmes. Architecture before content. It is a conviction I have since had good reason to test.",
    tags: [["tl", "Infrastructure"], ["tl", "Platform design"], ["gd", "SD Talks"], ["gd", "SDG Primer"]],
  },
  {
    year: "2017–2019 · Scaling",
    title: "UN SDG:Learn — reaching the million",
    body: "Built on the hub's foundations, UN SDG:Learn became the UN system's flagship SDG learning platform — growing from 4,000 users in 2019 to over 1.1 million cumulative users and 5 million pageviews. 68 partners. A 7-year evaluation provides the evidence base.",
    tags: [["tl", "1.1M+ users"], ["gd", "68 partners"], ["tl", "7-year evaluation"]],
  },
  {
    year: "2018–2020 · Deepening",
    title: "RC SDG certification and pioneering programme modalities",
    body: "Built with UN DCO as partner, the SDG Certification was designed for peer learning rather than credentialing. Resident Coordinators worked through the SDG Primer, passed an assessment, then wrote and exchanged essays that their peers read and commented on. Role-specific, and built on the premise that the most useful thing a Resident Coordinator can learn from is another Resident Coordinator. The Thai SDG Micro-Primer showed what happens when UN Country Teams become producers of learning rather than recipients, inspiring replication across agencies.",
    tags: [["vi", "RC certification"], ["vi", "Policy Coherence course"], ["gd", "Replication across agencies"]],
  },
  {
    year: "2020–2022 · Resilience and reinvention",
    title: "Learncast, Mastermind, games, and learning without walls",
    body: "When COVID-19 closed physical learning spaces, the answer was redesign rather than replication. The SDG Learncast podcast reached top 15% of Spotify Education globally. The UN Mastermind Group inverted the traditional expert-to-participant dynamic. AARs and Chats with Experts were institutionalized as internal knowledge capture mechanisms. A board game prototype was tested with UN Country Team members.",
    tags: [["gd", "100K+ podcast plays"], ["vi", "UN Mastermind Group"], ["cr", "Board game prototype"], ["gd", "AARs & Chats with Experts"]],
  },
  {
    year: "2021–2023 · Immersive frontier",
    title: "Inside Sustainable Development VR and COP deployment",
    body: "The VR experience brought something no e-learning module could: presence. A participant using a wheelchair attended a site visit through virtual reality for the first time. User testing showed measurable mindset shifts. At the UN Climate Change Conference in Egypt, the film and headsets were deployed alongside an online climate game. The research literature on immersive and experiential learning consistently associates presence and embodied experience with stronger knowledge retention, spatial comprehension, and transfer to judgement and decision-making under uncertainty. Modality is not neutral. It determines what kind of change is possible.",
    tags: [["vi", "Accessibility breakthrough"], ["vi", "Mindset evidence"], ["cr", "COP27 deployment"], ["cr", "Climate game"]],
  },
  {
    year: "2022–2025 · Capability at scale",
    title: "ASDA — longitudinal evidence of change",
    body: "The Africa Sustainable Development Academy, funded at USD 400,000 and reaching 20+ African countries, is the portfolio's most developed evidence story. Four-month and one-year post-programme follow-ups capture self-reported application of learning. ASDA is the proof of concept for what happens when learning design operates across the full capability chain rather than stopping at delivery.",
    tags: [["gd", "USD 400K secured"], ["gd", "20+ African countries"], ["cr", "4-month follow-up"], ["cr", "1-year follow-up"]],
  },
  {
    year: "2025–2026 · Convergence",
    title: "AI-VR project and SDKB — the next conditions",
    body: "An AI-VR project exploring climate, land, and biodiversity is in development — converging two proven modalities into a new frontier. The Sustainable Development Knowledge Bank (SDKB) is in development as the institutional memory layer. The next chapter is about embedding, not just reaching.",
    tags: [["vi", "AI-VR in development"], ["gd", "SDKB in development"], ["tl", "Microlearning across portfolios"]],
  },
];

const lessons = [
  ["tl", "Form follows function", "There is no one-size-fits-all learning solution. Working with UN DCO on initiatives for Resident Coordinators and Country Teams taught us to keep returning to the drawing board — starting from the actual learning need, then choosing the technology that serves it. Not the reverse."],
  ["gd", "Gather people around a bonfire, not a vending machine", "Course factories dispense content. Bonfires create spaces where people feel seen, safe, and able to speak. Psychological and emotional safety is foundational — it requires intentional design, trauma-informed facilitation, and inclusive pedagogy, especially for communities routinely left out of global conversations."],
  ["cr", "Let go of perfection in favour of relevance", "Learners are not content consumers. They are curators. The question is whether our materials can be reused, remixed, and reshared — adapted to fit contexts and languages we did not anticipate. Open Educational Resources make this possible, but only where there is institutional will to loosen control."],
  ["vi", "Capability, not just capacity", "Capacity building expands how much an organization can deliver. Capability development changes how effectively people navigate complexity, design inclusive policy, and empower communities. ASDA was built on that distinction — and four months on, participants were not reflecting on what they learned. They were changing how their institutions respond."],
  ["tl", "Design from the edge, not the average", "ASDA participants joined with unstable internet, mobile-only access, and visual impairments requiring assistive technology. Custom audio modules, mobile-friendly formats, and one-to-one technical support followed. The lesson was not about inclusion as access — it was about creating the conditions for full, meaningful contribution."],
  ["gd", "Make it irresistible", "If social media can teach someone to cook, dance, or invest in under a minute, sustainable development learning can compete for the same attention. Over 100,000 learners engaged with open, free experiences in two years — gamified challenges, microlearning, VR, AI, theatre, and music. When learning is accessible and engaging, people show up."],
  ["cr", "Modality is never neutral", "VR brought something no e-learning module could: presence. A parliamentarian using a wheelchair joined a site visit for the first time. The format did not just deliver the content differently — it determined what kind of change was possible at all."],
  ["vi", "Measure past the completion certificate", "Completion rates tell you almost nothing about impact. Four-month and one-year follow-ups tell you whether someone registered a nonprofit, reformed a process, or shifted a national policy conversation. That evidence is harder to collect and it is the only kind that answers the question donors are actually asking."],
  ["tl", "Test the assumption before you build the platform", "Before committing to a platform build, I put its founding assumptions in front of the people who would actually use it, and set out to test them rather than confirm them. Doing that with rigour is uncomfortable and it is cheaper than discovering the same things after a launch. What it surfaced now shapes how I approach the next build."],
  ["gd", "Watch the outliers, not the average user", "Inside every organization there are early adopters and hackers who solve their own knowledge and decision problems without waiting for permission or tools. They are not edge cases to be tidied away. They are the clearest available signal of where the work is heading, and observing them now shapes the platforms, programmes and interventions I design next."],
];

const rows = [
  ["tl", "Reach", "Access and registration", "Strong — 1.1M+ users, annual surveys, 7-year evaluation", "Improve survey response rates; disaggregate by region and role"],
  ["tl", "Engagement", "Completion, interaction, return", "Strong for facilitated formats —  60 to 99% completion on moderated courses in policy coherence for sustainable development and the Paris Agreement; platform, podcast and microlearning analytics all robust", "Extend tracking to game and quiz formats; understand what drives the gap between facilitated and self-paced"],
  ["gd", "Application", "Self-reported behaviour change", "Developing — ASDA 4-month and 1-year follow-up; UNSA 6–12 month post-course", "Standardize follow-up protocol across all programmes"],
  ["cr", "Replication", "Approaches replicated by partners", "Emerging — Thai micro-primer replicated by other UN agencies", "Document replication cases systematically"],
  ["vi", "Transformation", "Mindset shift, systemic change", "Emerging — UN Thailand reports national SDG awareness rising from 17 to 25% (2023 to 2025); VR user testing and accessibility cases", "Test immersive and AI-enabled formats against validated instruments; extend low-bandwidth, offline and accessibility provision now in development"],
];

export default function InnovationTab() {
  const [gate, setGate] = useState(null);
  const [unlocked, setUnlocked] = useState(() => ({
    policy: !!safeGet("tool_email_policy"),
    learning: !!safeGet("tool_email_learning"),
  }));
  const [preview, setPreview] = useState({ policy: false, learning: false });

  const handleLaunch = (tool) => {
    if (unlocked[tool.key]) window.open(tool.url, "_blank", "noopener");
    else setGate({ tool, action: "launch" });
  };
  const handlePreview = (tool) => {
    if (unlocked[tool.key]) setPreview((p) => ({ ...p, [tool.key]: !p[tool.key] }));
    else setGate({ tool, action: "preview" });
  };
  const onUnlocked = () => {
    const { tool, action } = gate;
    setUnlocked((u) => ({ ...u, [tool.key]: true }));
    setGate(null);
    if (action === "launch") window.open(tool.url, "_blank", "noopener");
    else setPreview((p) => ({ ...p, [tool.key]: true }));
  };

  return (
    <div className="tab-panel" data-testid="tab-panel-innovation">
      <AnimatePresence>
        {gate && <ToolGate tool={gate.tool} onClose={() => setGate(null)} onUnlocked={onUnlocked} />}
      </AnimatePresence>

      <div className="s-hero">
        <div className="s-hi">
          <div className="s-eyebrow-pill"><span className="s-dot"></span> Learning innovation · UNSSC KCSD</div>
          <h1 className="s-h1">Twelve years of <em>learning innovation</em></h1>
          <p className="s-hsub">What it takes to close the gap between access and impact — the arc of the work, the evidence behind it, and the lessons that shaped how I design learning today.</p>
          <div className="s-pills">
            <span className="s-pill">Knowledge management</span>
            <span className="s-pill">Learning innovation</span>
            <span className="s-pill">Capability development</span>
            <span className="s-pill">Ecosystem building</span>
            <span className="s-pill">Measurement for impact</span>
          </div>
        </div>
      </div>

      <Sec>
        <div className="s-lbl">The question underneath the work</div>
        <h2 className="s-h2">What does it take for learning<br />to become action?</h2>
        <p className="s-p">Every platform, every podcast, every game, every field visit reimagined through 360-degree video has been an attempt to answer one question. Not how many people can we reach — but what has to be true for what they learn to change what they do.</p>
        <p className="s-p">The answer, twelve years in, is that reach is the easy part. The hard part is context: whether learning fits how people actually work, whether they feel safe enough to engage with it, and whether anything in their institution allows them to act on it afterwards.</p>
        <div className="s-quote"><p>Learning and knowledge management are not support functions. They are the conditions under which any programme works at all.</p></div>
      </Sec>

      <hr className="s-divider" />

      <div id="innovation-tools" style={{ scrollMarginTop: "100px" }}></div>
      <Sec>
        <div className="s-lbl">From twelve years of practice</div>
        <h2 className="s-h2">Use what I've learned — right now</h2>
        <p className="s-p">Everything below these tools is the story of how these lessons were earned. But you don't have to read the story first: these two interactive tools distil twelve years of working on the policy and learning functions for sustainable development into something you can use immediately. Enter your email once per tool to open it.</p>
        <div className="tool-suite" data-testid="innovation-tool-suite">
          <div className="tool-suite-head">
            <div className="tool-suite-eyebrow"><span className="tool-suite-dot"></span> A connected toolkit · created by Paulyn Duman</div>
            <div className="tool-suite-title">Two functions, one method</div>
            <p className="tool-suite-sub">Both tools run the same discipline — <em>function first, instrument last</em>. One applies it to policy design, the other to learning design.</p>
          </div>
          {tools.map((tool, i) => {
            const isOpen = preview[tool.key];
            const isUnlocked = unlocked[tool.key];
            const Icon = tool.Icon;
            return (
              <div className="tool-suite-item" key={tool.key}>
                <div className="lib-tool tool-suite-card" data-testid={`innovation-tool-${tool.key}`}>
                  <div className="lib-tool-head">
                    <div className="lib-tool-icon"><Icon className="icon" /></div>
                    <div>
                      <div className="lib-tool-eyebrow">{tool.eyebrow}</div>
                      <div className="lib-tool-title">{tool.title}</div>
                    </div>
                  </div>
                  <p className="lib-tool-desc">{tool.desc}</p>
                  <div className="lib-tool-actions">
                    <button className="cf-submit" onClick={() => handleLaunch(tool)} data-testid={`innovation-tool-${tool.key}-launch`}>
                      {isUnlocked ? <ExternalLink className="icon" /> : <Lock className="icon" />} {isUnlocked ? "Launch the tool" : "Enter email to launch"}
                    </button>
                    <button className="lib-tool-preview-btn" onClick={() => handlePreview(tool)} data-testid={`innovation-tool-${tool.key}-preview-toggle`}>
                      {isOpen ? <EyeOff className="icon" /> : <Eye className="icon" />} {isOpen ? "Hide preview" : "Preview it here"}
                    </button>
                  </div>
                  {isUnlocked && isOpen && (
                    <iframe src={tool.url} title={`${tool.title} — interactive tool by Paulyn Duman`} className="lib-tool-frame" data-testid={`innovation-tool-${tool.key}-iframe`} loading="lazy" />
                  )}
                </div>
                {i === 0 && (
                  <div className="tool-suite-connector" aria-hidden="true">
                    <span className="tsc-line"></span>
                    <span className="tsc-node"><Link2 className="icon" /></span>
                    <span className="tsc-line"></span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Sec>

      <hr className="s-divider" />

      <Sec>
        <div className="s-lbl">12 years · one story</div>
        <h2 className="s-h2">From infrastructure to impact</h2>
        <p className="s-lead">The history of this work is a history of building conditions before building products — and pioneering learning modalities before they became standard practice in the UN system.</p>
        <div className="s-tl">
          {timeline.map((t, i) => (
            <div className="s-tli" key={i}>
              <div className="s-tly">{t.year}</div>
              <div className="s-tlt">{t.title}</div>
              <div className="s-tlb">{t.body}</div>
              <div className="s-tltags">{t.tags.map(([c, l], j) => <span className={`s-tag ${c}`} key={j}>{l}</span>)}</div>
            </div>
          ))}
        </div>
      </Sec>

      <hr className="s-divider" />

      <Sec className="s-sec cream">
        <div className="s-lbl">Measurement framework</div>
        <h2 className="s-h2">Evidence across the capability chain</h2>
        <p className="s-p">Evidence sits across five levels. Being honest about where it is strong, where it is partial, and where it is still thin is what makes the next investment in measurement possible.</p>
        <div style={{ overflowX: "auto" }}>
          <table className="s-table">
            <thead>
              <tr><th>Level</th><th>What we measure</th><th>Current evidence</th><th>Next step</th></tr>
            </thead>
            <tbody>
              {rows.map(([c, lvl, what, ev, next], i) => (
                <tr key={i}><td><span className={`s-badge ${c}`}>{lvl}</span></td><td>{what}</td><td>{ev}</td><td>{next}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="s-quote" style={{ marginTop: "1.5rem" }}><p>The honest story: we are stronger on reach than on results. Closing that gap does not mean abandoning reach — it means ensuring every access point has a pathway to application.</p></div>
      </Sec>

      <hr className="s-divider" />

      <Sec>
        <div className="s-lbl">Lessons on innovation</div>
        <h2 className="s-h2">What twelve years<br />has actually taught me</h2>
        <p className="s-p">These are drawn from what worked, what did not, and what surprised us — set out across a series of articles published on the UNSSC website and presented at Learn4Dev, EdTechX London, and other forums.</p>
        <div className="s-pgrid" style={{ marginTop: "2rem" }}>
          {lessons.map(([c, t, b], i) => (
            <div className={`s-pc ${c}`} key={i}>
              <div className="s-pt">{t}</div>
              <div className="s-pb">{b}</div>
            </div>
          ))}
        </div>
      </Sec>
    </div>
  );
}
